import {
  Badge,
  Button,
  Card,
  Container,
  Group,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '@components/shared/PageHeader';
import { getFoodNameById, getFoodNamesByIds } from '../data/foods';
import { useAbly } from '../providers/AblyProvider';
import {
  getConfiguration,
  getConfigurations,
  updateConfiguration,
} from '../services/configurationManager';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { getExposureConfigFromSession } from '../utils/extractSessionObject';
import {
  analyzeExposureItemCoverage,
  classifyExposureDeviceEvent,
  extractRawExposureItemId,
  flattenConfiguredExposureItemIds,
} from '../utils/exposureDeviceEventCheck';
import { getPlannedExposureGroups } from '../utils/exposureMultiItem';
import { formatTimestampToTime } from '../utils/formatDuration';
import { extractMessageName } from '../utils/sessionMessageHandlers';
import { createSessionEventManager } from '../utils/sessionEventManager';

type CaptureKind =
  | 'tutorialCompleted'
  | 'itemShown'
  | 'itemFeedback'
  | 'sessionStartedAt'
  | 'sessionEndedAt'
  | 'mealEnded'
  | 'cancelSession'
  | 'sessionEnded';

type CapturedEvent = {
  receivedAt: number;
  eventTimestamp: number;
  type: CaptureKind;
  itemId: number | null;
  score: number | null;
  detail: string | null;
};

const OPEN_STATUSES = ['queued', 'initializing', 'in-progress'] as const;
const ITEM_EVENT_TYPES: CaptureKind[] = ['itemShown', 'itemFeedback'];

function payloadEventTime(payload: Record<string, unknown>): number {
  const ts = payload.timestamp;
  if (typeof ts === 'number' && Number.isFinite(ts)) return ts;
  if (typeof ts === 'string') {
    const asNumber = Number(ts);
    if (Number.isFinite(asNumber) && ts.trim() !== '') return asNumber;
    const parsed = Date.parse(ts);
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }
  return Date.now();
}

function eventKind(messageName: string): CaptureKind | null {
  if (messageName === 'tutorialCompleted') return 'tutorialCompleted';
  if (messageName === 'itemShown' || messageName === 'foodShown') {
    return 'itemShown';
  }
  if (messageName === 'itemFeedback' || messageName === 'foodFeedback') {
    return 'itemFeedback';
  }
  if (messageName === 'sessionStartedAt') return 'sessionStartedAt';
  if (messageName === 'sessionEndedAt') return 'sessionEndedAt';
  if (messageName === 'mealEnded') return 'mealEnded';
  if (messageName === 'cancelSession') return 'cancelSession';
  if (messageName === 'sessionEnded') return 'sessionEnded';
  return null;
}

function eventTypeLabel(type: CaptureKind): string {
  switch (type) {
    case 'tutorialCompleted':
      return 'Tutorial';
    case 'itemShown':
      return 'Shown';
    case 'itemFeedback':
      return 'Feedback';
    case 'sessionStartedAt':
      return 'Start sessione';
    case 'sessionEndedAt':
      return 'End sessione';
    case 'mealEnded':
      return 'Meal ended';
    case 'cancelSession':
      return 'Cancel';
    case 'sessionEnded':
      return 'Session ended';
  }
}

function contextEventDetail(
  kind: CaptureKind,
  payload: Record<string, unknown>
): string | null {
  if (kind === 'mealEnded') {
    return typeof payload.clientId === 'string'
      ? `clientId: ${payload.clientId}`
      : 'Pasto terminato';
  }
  if (kind === 'cancelSession') {
    const code = typeof payload.code === 'string' ? payload.code : '—';
    return `code: ${code}`;
  }
  if (kind === 'sessionEnded') {
    const messages = Array.isArray(payload.messages)
      ? payload.messages.map(String).join(', ')
      : '';
    return messages ? `messages: ${messages}` : 'Sessione terminata';
  }
  if (kind === 'sessionStartedAt') return 'Sessione avviata dal device';
  if (kind === 'sessionEndedAt') return 'Sessione terminata dal device';
  if (kind === 'tutorialCompleted') return 'Tutorial completato';
  return null;
}

export default function AdminExposureEvents() {
  const { sendMessage, subscribeToMessages } = useAbly();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [events, setEvents] = useState<CapturedEvent[]>([]);
  const [listening, setListening] = useState(false);
  const [hubStartSent, setHubStartSent] = useState(false);

  const listQueries = useQueries({
    queries: OPEN_STATUSES.map(status => ({
      queryKey: ['admin-exposure-events', 'list', status] as const,
      queryFn: () => getConfigurations({ status, page: 1, size: 100 }),
    })),
  });

  const sessions = useMemo(() => {
    const byId = new Map<number, DeviceConfigurationApi>();
    for (const q of listQueries) {
      for (const item of q.data?.items ?? []) {
        if (item.type === 'exposure') {
          byId.set(item.id, item);
        }
      }
    }
    return [...byId.values()].sort((a, b) => b.id - a.id);
  }, [listQueries]);

  const sessionQuery = useQuery({
    queryKey: ['admin-exposure-events', 'session', selectedId],
    queryFn: async () => {
      if (!selectedId) return null;
      const res = await getConfiguration(selectedId);
      return res.data;
    },
    enabled: Boolean(selectedId),
  });

  const session = sessionQuery.data ?? null;
  const exposureConfig = useMemo(
    () => (session ? getExposureConfigFromSession(session) : null),
    [session]
  );
  const isMultiItem = Boolean(exposureConfig?.isMultiItem);
  const hasTutorial = Boolean(exposureConfig?.hasTutorial);
  const plannedGroups = useMemo(
    () => getPlannedExposureGroups(exposureConfig),
    [exposureConfig]
  );
  const configuredIds = useMemo(
    () => flattenConfiguredExposureItemIds(exposureConfig?.itemIds),
    [exposureConfig]
  );
  const expectedGroups = useMemo(() => {
    if (plannedGroups.length > 0) return plannedGroups;
    if (configuredIds.length === 0) return [];
    return [{ label: 'Elementi', itemIds: configuredIds }];
  }, [plannedGroups, configuredIds]);

  const itemEvents = useMemo(
    () => events.filter(e => ITEM_EVENT_TYPES.includes(e.type)),
    [events]
  );
  const tutorialEvents = useMemo(
    () => events.filter(e => e.type === 'tutorialCompleted'),
    [events]
  );
  const lifecycleSummary = useMemo(() => {
    const has = (type: CaptureKind) => events.some(e => e.type === type);
    return {
      started: has('sessionStartedAt'),
      mealEnded: has('mealEnded'),
      endedAt: has('sessionEndedAt'),
      sessionEnded: has('sessionEnded'),
      cancelled: has('cancelSession'),
    };
  }, [events]);

  const coverage = useMemo(() => {
    const shownIds = itemEvents
      .filter(e => e.type === 'itemShown' && e.itemId != null)
      .map(e => e.itemId as number);
    const feedbackIds = itemEvents
      .filter(e => e.type === 'itemFeedback' && e.itemId != null)
      .map(e => e.itemId as number);
    return analyzeExposureItemCoverage({
      isMultiItem,
      configuredItemIds: exposureConfig?.itemIds,
      shownIds,
      feedbackIds,
    });
  }, [exposureConfig?.itemIds, isMultiItem, itemEvents]);

  const tutorialSummary = useMemo(() => {
    const received = tutorialEvents.length > 0;
    if (hasTutorial && !received) return 'atteso, non ricevuto';
    if (!hasTutorial && received) return 'ricevuto, ma hasTutorial era false';
    if (hasTutorial && received) return 'ok';
    return 'non previsto';
  }, [hasTutorial, tutorialEvents.length]);

  const formatIds = useCallback(
    (ids: number[]) => {
      if (ids.length === 0) return 'nessuno';
      const max = 12;
      const slice = ids.slice(0, max);
      const formatted = isMultiItem
        ? slice.join(', ')
        : slice
            .map(id => {
              const name = getFoodNameById(id);
              return name === String(id) ? String(id) : `${id} (${name})`;
            })
            .join(', ');
      if (ids.length <= max) return formatted;
      return `${formatted} … (+${ids.length - max})`;
    },
    [isMultiItem]
  );

  useEffect(() => {
    setEvents([]);
    setListening(false);
    setHubStartSent(false);
  }, [selectedId]);

  useEffect(() => {
    if (!listening || !selectedId || !session) return;

    const unsubscribe = subscribeToMessages(
      message => {
        const messageName = extractMessageName(message);
        const kind = eventKind(messageName);
        if (!kind) return false;
        const payload = message.data;
        if (typeof payload !== 'object' || payload === null) return false;
        const payloadObj = payload as Record<string, unknown>;

        if (kind === 'cancelSession') {
          return (
            typeof payloadObj.code === 'string' &&
            payloadObj.command === 'cancel' &&
            (!session.deviceCode || payloadObj.code === session.deviceCode)
          );
        }

        if (kind === 'sessionEnded') {
          return (
            typeof payloadObj.deviceCode === 'string' &&
            typeof payloadObj.sessionId === 'string' &&
            payloadObj.sessionId === selectedId
          );
        }

        const hasTimestamp =
          typeof payloadObj.timestamp === 'number' ||
          (typeof payloadObj.timestamp === 'string' &&
            !Number.isNaN(Date.parse(payloadObj.timestamp)));
        if (!hasTimestamp) return false;

        if (
          typeof payloadObj.sessionId === 'string' &&
          payloadObj.sessionId !== selectedId
        ) {
          return false;
        }
        if (
          session.deviceCode &&
          typeof payloadObj.clientId === 'string' &&
          payloadObj.clientId !== session.deviceCode
        ) {
          return false;
        }
        if (kind === 'itemShown' || kind === 'itemFeedback') {
          const hasItemIds =
            typeof payloadObj.itemIds === 'number' ||
            typeof payloadObj.foodIds === 'number';
          if (!hasItemIds) return false;
        }
        if (kind === 'itemFeedback' && typeof payloadObj.score !== 'number') {
          return false;
        }
        return true;
      },
      message => {
        const kind = eventKind(extractMessageName(message));
        if (!kind) return;
        const payload = message.data as Record<string, unknown>;
        const itemId =
          kind === 'itemShown' || kind === 'itemFeedback'
            ? extractRawExposureItemId(payload)
            : null;
        const score =
          kind === 'itemFeedback' && typeof payload.score === 'number'
            ? payload.score
            : null;
        setEvents(prev => [
          ...prev,
          {
            receivedAt: Date.now(),
            eventTimestamp: payloadEventTime(payload),
            type: kind,
            itemId,
            score,
            detail: contextEventDetail(kind, payload),
          },
        ]);
      }
    );

    return unsubscribe;
  }, [listening, selectedId, session, subscribeToMessages]);

  const startMutation = useMutation({
    mutationFn: async () => {
      if (!session || !selectedId) {
        throw new Error('Seleziona una sessione exposure');
      }
      await updateConfiguration(session.id, {
        status: 'in-progress',
        started_at: new Date().toISOString(),
      });
      createSessionEventManager(
        'exposure',
        String(session.id),
        session,
        sendMessage
      ).start();
    },
    onSuccess: () => {
      setEvents([]);
      setListening(true);
      setHubStartSent(true);
      notifications.show({
        color: 'teal',
        title: 'Sessione avviata',
        message:
          'In ascolto di tutorial, shown/feedback, start/end, mealEnded e cancel.',
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Avvio fallito',
        message: error instanceof Error ? error.message : 'Impossibile avviare',
      });
    },
  });

  const selectData = sessions.map(item => ({
    value: String(item.id),
    label: `#${item.id} · ${item.deviceCode} · ${item.scenario} · ${item.status}`,
  }));

  const listLoading = listQueries.some(q => q.isLoading);

  const onSelect = useCallback((value: string | null) => {
    setSelectedId(value);
  }, []);

  return (
    <Container size='xl' py='lg'>
      <Stack gap='lg'>
        <PageHeader
          title='Exposure'
          breadcrumbs={[
            { label: 'Admin', href: '/admin' },
            {
              label: 'App Playground testing',
              href: '/admin/app-playground',
            },
            { label: 'Exposure' },
          ]}
          showBackButton
          backUrl='/admin/app-playground'
        />
        <Text size='sm' c='dimmed'>
          Avvia una sessione exposure esistente e confronta gli eventi grezzi
          Ably (tutorial, shown/feedback, start/end, mealEnded, cancel) con il
          contratto: id catalogo in modalità singola, indice gruppo 1-based in
          modalità multipla. I valori non vengono corretti.
        </Text>

        <Card withBorder p='md'>
          <Stack gap='sm'>
            <Select
              label='Sessione exposure (non completata)'
              placeholder={listLoading ? 'Caricamento…' : 'Scegli una sessione'}
              data={selectData}
              value={selectedId}
              onChange={onSelect}
              searchable
              nothingFoundMessage='Nessuna sessione exposure aperta'
            />
            <Group>
              <Button
                onClick={() => startMutation.mutate()}
                loading={startMutation.isPending}
                disabled={!session}
              >
                Avvia
              </Button>
              {listening && (
                <Badge color='teal' variant='light'>
                  In ascolto
                </Badge>
              )}
            </Group>
          </Stack>
        </Card>

        {session && (
          <Card withBorder p='md'>
            <Stack gap='xs'>
              <Title order={5}>Lista attesa</Title>
              <Text size='xs' c='dimmed'>
                Device {session.deviceCode} · mappa {session.scenario} ·{' '}
                {isMultiItem ? 'multipla (gruppi)' : 'singola'} · tutorial{' '}
                {hasTutorial ? 'sì' : 'no'}
              </Text>
              {expectedGroups.length === 0 ? (
                <Text size='sm' c='dimmed'>
                  Nessun elemento in config
                </Text>
              ) : (
                expectedGroups.map(group => (
                  <Stack key={group.label} gap='xs'>
                    {isMultiItem && (
                      <Text size='sm' fw={600}>
                        {group.label}
                      </Text>
                    )}
                    <Table withTableBorder withColumnBorders>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>ID</Table.Th>
                          <Table.Th>Nome</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {group.itemIds.map(itemId => (
                          <Table.Tr key={itemId}>
                            <Table.Td>{itemId}</Table.Td>
                            <Table.Td>{getFoodNameById(itemId)}</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Stack>
                ))
              )}
            </Stack>
          </Card>
        )}

        <Card withBorder p='md'>
          <Stack gap='xs'>
            <Group justify='space-between'>
              <Title order={5}>Eventi Ably</Title>
              <Badge variant='light'>{events.length}</Badge>
            </Group>
            {events.length === 0 ? (
              <Text size='sm' c='dimmed'>
                Nessun evento Ably ancora
              </Text>
            ) : (
              <Table withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Orario</Table.Th>
                    <Table.Th>Tipo</Table.Th>
                    <Table.Th>
                      {isMultiItem ? 'Indice gruppo' : 'ID ricevuto'}
                    </Table.Th>
                    <Table.Th>Dettaglio</Table.Th>
                    <Table.Th>Score</Table.Th>
                    <Table.Th>Verdetto</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {events.map((event, index) => {
                    if (!ITEM_EVENT_TYPES.includes(event.type)) {
                      let verdictLabel: string | null = null;
                      let verdictColor: string = 'gray';
                      if (event.type === 'tutorialCompleted') {
                        verdictLabel = hasTutorial
                          ? 'atteso'
                          : 'non previsto (hasTutorial=false)';
                        verdictColor = hasTutorial ? 'teal' : 'red';
                      } else if (
                        event.type === 'sessionStartedAt' ||
                        event.type === 'sessionEndedAt' ||
                        event.type === 'mealEnded' ||
                        event.type === 'sessionEnded'
                      ) {
                        verdictLabel = 'ricevuto';
                        verdictColor = 'teal';
                      } else if (event.type === 'cancelSession') {
                        verdictLabel = 'cancel';
                        verdictColor = 'orange';
                      }
                      return (
                        <Table.Tr
                          key={`${event.type}-${event.eventTimestamp}-${index}`}
                        >
                          <Table.Td>
                            {formatTimestampToTime(event.eventTimestamp)}
                          </Table.Td>
                          <Table.Td>{eventTypeLabel(event.type)}</Table.Td>
                          <Table.Td>—</Table.Td>
                          <Table.Td>{event.detail ?? '—'}</Table.Td>
                          <Table.Td>—</Table.Td>
                          <Table.Td>
                            {verdictLabel ? (
                              <Badge color={verdictColor} variant='light'>
                                {verdictLabel}
                              </Badge>
                            ) : (
                              '—'
                            )}
                          </Table.Td>
                        </Table.Tr>
                      );
                    }

                    const verdict =
                      event.itemId == null
                        ? null
                        : classifyExposureDeviceEvent({
                            isMultiItem,
                            configuredItemIds: exposureConfig?.itemIds,
                            receivedItemId: event.itemId,
                          });
                    const group =
                      isMultiItem && event.itemId != null
                        ? expectedGroups[event.itemId - 1]
                        : undefined;
                    const detail =
                      event.itemId == null
                        ? '—'
                        : isMultiItem
                          ? group
                            ? `Gruppo ${event.itemId}: ${getFoodNamesByIds(group.itemIds)}`
                            : `Gruppo ${event.itemId}`
                          : getFoodNameById(event.itemId);
                    return (
                      <Table.Tr
                        key={`${event.type}-${event.eventTimestamp}-${index}`}
                      >
                        <Table.Td>
                          {formatTimestampToTime(event.eventTimestamp)}
                        </Table.Td>
                        <Table.Td>{eventTypeLabel(event.type)}</Table.Td>
                        <Table.Td>
                          {event.itemId == null ? '—' : event.itemId}
                        </Table.Td>
                        <Table.Td>{detail}</Table.Td>
                        <Table.Td>
                          {event.score == null ? '—' : event.score}
                        </Table.Td>
                        <Table.Td>
                          {verdict == null ? (
                            '—'
                          ) : (
                            <Badge
                              color={verdict === 'in-list' ? 'teal' : 'red'}
                              variant='light'
                            >
                              {verdict === 'in-list'
                                ? 'in lista'
                                : 'fuori lista'}
                            </Badge>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </Card>

        {(listening || events.length > 0) && (
          <Card withBorder p='md'>
            <Stack gap='xs'>
              <Title order={5}>Riepilogo</Title>

              <Text size='sm' fw={600}>
                Lifecycle
              </Text>
              <Text size='sm'>
                Start hub (startSession):{' '}
                {hubStartSent ? 'inviato' : 'non inviato'}
              </Text>
              <Text size='sm'>
                Start device (sessionStartedAt):{' '}
                {lifecycleSummary.started
                  ? 'ricevuto'
                  : 'non ricevuto dal device'}
              </Text>
              <Text size='sm'>Tutorial: {tutorialSummary}</Text>
              <Text size='sm'>
                Meal ended:{' '}
                {lifecycleSummary.mealEnded ? 'ricevuto' : 'non ricevuto'}
              </Text>
              <Text size='sm'>
                End device:{' '}
                {lifecycleSummary.endedAt
                  ? 'sessionEndedAt ricevuto'
                  : lifecycleSummary.sessionEnded
                    ? 'sessionEnded ricevuto'
                    : 'non ricevuto'}
                {lifecycleSummary.cancelled ? ' · cancelSession' : ''}
              </Text>
              {!lifecycleSummary.endedAt &&
                !lifecycleSummary.sessionEnded &&
                lifecycleSummary.mealEnded && (
                  <Text size='xs' c='dimmed'>
                    Nota: mealEnded non chiude la sessione; manca ancora
                    sessionEndedAt / sessionEnded.
                  </Text>
                )}

              <Text size='sm' fw={600} mt='xs'>
                Copertura {isMultiItem ? 'gruppi' : 'item'}
              </Text>
              <Text size='sm'>
                Ricevuti (shown e/o feedback): {coverage.coveredCount} /{' '}
                {coverage.expectedCount}
              </Text>
              {coverage.possibleOffByOne && (
                <Text size='sm' c='red'>
                  Possibile sfasamento +1 sul device: manca il primo id
                  configurato e arriva un id oltre l&apos;ultimo (es. config
                  1…164 → device 2…165).
                </Text>
              )}
              <Text size='sm'>
                Mai arrivati (né shown né feedback):{' '}
                {formatIds(coverage.missingAny)}
              </Text>
              <Text size='sm'>
                Senza shown: {formatIds(coverage.missingShown)}
              </Text>
              <Text size='sm'>
                Senza feedback: {formatIds(coverage.missingFeedback)}
              </Text>
              <Text size='sm'>Fuori lista: {formatIds(coverage.extra)}</Text>
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
